import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ArrowLeft, ChevronsDownUp, ChevronsUpDown, Clock, CloudRain, Plus } from 'lucide-react';
import {
  capitalizeFirstWord,
  entriesInTimeOrder,
  formatTimelineSpan,
  formatTimelineTime,
  timelineEntryCount,
  type EventTag,
  type Timeline,
  type TimelineEntry,
  type TimelineSection,
} from '@peles/shared';
import {
  useAddTimelineEntry,
  useAddTimelineSection,
  useDeleteTimelineEntry,
  useDeleteTimelineSection,
  useUpdateTimeline,
  useUpdateTimelineEntry,
  useUpdateTimelineSection,
} from '../../hooks/useEventOps';
import { useDialog } from '../../context/DialogContext';
import { InlineEditText } from '../common/InlineEditText';
import { TimelineSectionCard, type EntryDraft } from './TimelineSectionCard';
import { TimelineNotesField } from './TimelineNotesField';
import { TimelineTagPanel } from './TimelineTagPanel';
import { useTimelineDnd } from './useTimelineDnd';
import {
  BLANK_ENTRY,
  changedEntryFields,
  entryToForm,
  newEntryInput,
  timeToInputValue,
  type EntryFormState,
} from './entryForm';

export interface TimelineEditorProps {
  timeline: Timeline;
  /** Where the back arrow goes; no arrow without one (inside the event workspace). */
  backPath?: string;
  /** The small label beside the name — "Timeline", "Timeline Template". */
  badge: string;
  /** Header buttons beside the badge (print, vendor times, export…). */
  actions?: ReactNode;
  /** Cards shown before the rain plan (the date, the event type). */
  details?: ReactNode;
  /** The event's tags, offered beside the sections. */
  tags?: EventTag[];
  /** Read-only: a client looking at the day. */
  readOnly?: boolean;
}

const SECTION_SUGGESTIONS = ['Ceremony', 'Cocktail hour', 'Reception', 'Vendors'];

/**
 * The one timeline editor — an event's run of show and a template alike;
 * the pages only add what differs (the date, the event type, print, vendor
 * times, export). Sections are cards that open expanded, each a time rail
 * of its entries; everything is edited where it is shown, and dragging is
 * saved as one order per drop.
 */
export function TimelineEditor({
  timeline,
  backPath,
  badge,
  actions,
  details,
  tags = [],
  readOnly = false,
}: TimelineEditorProps) {
  const navigate = useNavigate();
  const { confirm } = useDialog();
  const updateTimeline = useUpdateTimeline();
  const updateSection = useUpdateTimelineSection();
  const addSection = useAddTimelineSection();
  const deleteSection = useDeleteTimelineSection();
  const addEntry = useAddTimelineEntry();
  const updateEntry = useUpdateTimelineEntry();
  const deleteEntry = useDeleteTimelineEntry();
  const dnd = useTimelineDnd(timeline);
  const { sections } = dnd;

  // ─── Collapsing: open by default, so a new section is open too ──────────
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const allCollapsed = sections.length > 0 && sections.every((s) => collapsed.has(s.id));

  // ─── The one entry form open at a time ─────────────────────────────────
  const [draft, setDraft] = useState<EntryDraft | null>(null);
  const [form, setForm] = useState<EntryFormState>(BLANK_ENTRY);
  const [initialForm, setInitialForm] = useState<EntryFormState>(BLANK_ENTRY);

  const startAdd = useCallback(
    (sectionId: string, afterEntryId?: string) => {
      const section = sections.find((s) => s.id === sectionId);
      const anchor = afterEntryId
        ? section?.entries.find((e) => e.id === afterEntryId)
        : section?.entries.at(-1);
      // A new cue usually follows the one it is added after: start from its time.
      setForm({ ...BLANK_ENTRY, time: timeToInputValue(anchor?.time) });
      setDraft({ kind: 'add', sectionId, afterEntryId: afterEntryId ?? null });
      setCollapsed((prev) => {
        if (!prev.has(sectionId)) return prev;
        const next = new Set(prev);
        next.delete(sectionId);
        return next;
      });
    },
    [sections],
  );

  const startEdit = useCallback((entry: TimelineEntry) => {
    const values = entryToForm(entry);
    setForm(values);
    setInitialForm(values);
    setDraft({ kind: 'edit', entryId: entry.id });
  }, []);

  const cancelDraft = useCallback(() => setDraft(null), []);

  // The entry an "add after" form hangs under was deleted (here or by
  // someone else): keep the unsaved draft, at the end of its section.
  useEffect(() => {
    if (draft?.kind !== 'add' || !draft.afterEntryId) return;
    const section = sections.find((s) => s.id === draft.sectionId);
    if (!section) setDraft(null);
    else if (!section.entries.some((e) => e.id === draft.afterEntryId)) {
      setDraft({ ...draft, afterEntryId: null });
    }
  }, [sections, draft]);

  const saveDraft = useCallback(() => {
    if (!draft || !form.title.trim()) return;
    if (draft.kind === 'add') {
      addEntry.mutate(
        {
          sectionId: draft.sectionId,
          ...newEntryInput(form),
          afterEntryId: draft.afterEntryId ?? undefined,
        },
        { onSuccess: () => setDraft(null) },
      );
      return;
    }
    const updates = changedEntryFields(initialForm, form);
    if (Object.keys(updates).length === 0) {
      setDraft(null);
      return;
    }
    updateEntry.mutate({ entryId: draft.entryId, updates }, { onSuccess: () => setDraft(null) });
  }, [draft, form, initialForm, addEntry, updateEntry]);

  const removeEntry = useCallback(
    async (entry: TimelineEntry) => {
      const ok = await confirm({
        title: 'Delete entry',
        message: `Delete “${entry.title}” from the timeline?`,
        confirmLabel: 'Delete',
        variant: 'danger',
      });
      if (!ok) return;
      if (draft?.kind === 'edit' && draft.entryId === entry.id) setDraft(null);
      deleteEntry.mutate(entry.id);
    },
    [confirm, deleteEntry, draft],
  );

  // ─── Sections ──────────────────────────────────────────────────────────
  const [newSectionName, setNewSectionName] = useState('');

  const createSection = (name: string) => {
    const clean = capitalizeFirstWord(name.trim());
    if (!clean) return;
    addSection.mutate(
      { timelineId: timeline.id, name: clean },
      { onSuccess: () => setNewSectionName('') },
    );
  };

  const renameSection = (sectionId: string, name: string) =>
    updateSection.mutateAsync({
      sectionId,
      updates: { name: capitalizeFirstWord(name.trim()) },
    });

  const removeSection = async (section: TimelineSection) => {
    const count = section.entries.length;
    const ok = await confirm({
      title: 'Delete section',
      message:
        count > 0
          ? `Delete “${section.name}” and its ${count === 1 ? 'entry' : `${count} entries`}?`
          : `Delete “${section.name}”?`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (ok) deleteSection.mutate(section.id);
  };

  const sortByTime = (section: TimelineSection) =>
    dnd.setSectionOrder(
      section.id,
      entriesInTimeOrder(section.entries).map((e) => e.id),
    );

  // ─── Header ────────────────────────────────────────────────────────────
  const isTemplate = timeline.isTemplate;
  const displayName = capitalizeFirstWord(
    isTemplate
      ? timeline.templateName || timeline.title || 'Untitled template'
      : timeline.title || 'Untitled timeline',
  );
  const rename = (name: string) => {
    const clean = capitalizeFirstWord(name.trim());
    return updateTimeline.mutateAsync({
      id: timeline.id,
      updates: isTemplate ? { templateName: clean } : { title: clean },
    });
  };

  const allEntries = useMemo(() => sections.flatMap((s) => s.entries), [sections]);
  const entryCount = timelineEntryCount(sections);
  const span = formatTimelineSpan(allEntries);
  const unusedSuggestions = SECTION_SUGGESTIONS.filter(
    (name) => !sections.some((s) => s.name.trim().toLowerCase() === name.toLowerCase()),
  );
  const showTags = tags.length > 0 && !readOnly;

  return (
    <DndContext sensors={dnd.sensors} collisionDetection={closestCenter} {...dnd.handlers}>
      <div className="print:hidden">
        {/* ─── Header ─────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-start gap-3 mb-4">
          {backPath && (
            <button
              type="button"
              onClick={() => navigate(backPath)}
              className="flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 sm:p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              aria-label="Back"
              data-testid="back-button"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {readOnly ? (
                <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
              ) : (
                <h1 className="min-w-0">
                  <InlineEditText
                    value={displayName}
                    onSave={rename}
                    label={isTemplate ? 'Template name' : 'Timeline name'}
                    className="text-2xl font-bold text-gray-900"
                    maxLength={isTemplate ? 255 : 500}
                  />
                </h1>
              )}
              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded-md">
                <Clock size={12} aria-hidden />
                {badge}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500" data-testid="timeline-summary">
              {sections.length === 1 ? '1 section' : `${sections.length} sections`} ·{' '}
              {entryCount === 1 ? '1 entry' : `${entryCount} entries`}
              {span && ` · ${span}`}
              {dnd.saving && <span className="ml-2 text-gray-400">Saving order…</span>}
              {dnd.failed && !dnd.saving && (
                <span role="alert" className="ml-2 text-red-600">
                  The new order was not saved — it has been put back.
                </span>
              )}
            </p>
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>}
        </div>

        {/* ─── Details ────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {details}
          <TimelineNotesField
            label="Rain plan"
            icon={<CloudRain size={14} aria-hidden />}
            value={timeline.rainPlanNotes}
            onSave={(rainPlanNotes) =>
              updateTimeline.mutateAsync({
                id: timeline.id,
                updates: { rainPlanNotes: rainPlanNotes || null },
              })
            }
            placeholder="What changes if the weather turns — where the ceremony moves, who to call…"
            readOnly={readOnly}
            testId="rain-plan-notes"
          />
        </div>

        <div className={showTags ? 'lg:flex lg:gap-4 lg:items-start' : ''}>
          <div className="flex-1 min-w-0 space-y-3">
            {sections.length > 1 && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() =>
                    setCollapsed(allCollapsed ? new Set() : new Set(sections.map((s) => s.id)))
                  }
                  className="flex items-center gap-1.5 min-h-11 sm:min-h-0 px-2 py-1 rounded-md text-xs text-gray-600 hover:bg-gray-100"
                  data-testid="toggle-all-sections"
                >
                  {allCollapsed ? (
                    <ChevronsUpDown size={14} aria-hidden />
                  ) : (
                    <ChevronsDownUp size={14} aria-hidden />
                  )}
                  {allCollapsed ? 'Expand all' : 'Collapse all'}
                </button>
              </div>
            )}

            {sections.length === 0 && (
              <div className="text-center py-10 px-4 bg-white rounded-xl border border-dashed border-gray-300">
                <Clock className="mx-auto h-10 w-10 text-gray-300" aria-hidden />
                <p className="mt-2 text-sm font-medium text-gray-700">No sections yet</p>
                <p className="mt-1 text-sm text-gray-500">
                  {readOnly
                    ? 'The office has not filled this timeline in yet.'
                    : 'Split the day into parts — each holds the cues that happen in it.'}
                </p>
              </div>
            )}

            <SortableContext
              items={sections.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {sections.map((section) => (
                <TimelineSectionCard
                  key={section.id}
                  section={section}
                  expanded={!collapsed.has(section.id)}
                  onToggle={toggle}
                  readOnly={readOnly}
                  draft={draft}
                  form={form}
                  onChangeForm={setForm}
                  saving={addEntry.isPending || updateEntry.isPending}
                  onSaveDraft={saveDraft}
                  onCancelDraft={cancelDraft}
                  onStartAdd={startAdd}
                  onStartEdit={startEdit}
                  onDeleteEntry={(entry) => void removeEntry(entry)}
                  onRename={renameSection}
                  onDelete={(s) => void removeSection(s)}
                  onSortByTime={sortByTime}
                />
              ))}
            </SortableContext>

            {!readOnly && (
              <div className="rounded-xl border border-dashed border-gray-300 p-3">
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    createSection(newSectionName);
                  }}
                >
                  <input
                    type="text"
                    value={newSectionName}
                    onChange={(e) => setNewSectionName(e.target.value)}
                    placeholder="New section — e.g. Reception"
                    aria-label="New section name"
                    maxLength={255}
                    className="flex-1 min-w-0 min-h-11 sm:min-h-0 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    data-testid="new-section-input"
                  />
                  <button
                    type="submit"
                    disabled={!newSectionName.trim() || addSection.isPending}
                    className="flex items-center gap-1.5 min-h-11 sm:min-h-0 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    data-testid="add-section-button"
                  >
                    <Plus size={15} aria-hidden />
                    Add section
                  </button>
                </form>
                {unusedSuggestions.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-gray-400">Quick add:</span>
                    {unusedSuggestions.map((name) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => createSection(name)}
                        disabled={addSection.isPending}
                        className="min-h-11 sm:min-h-0 px-2.5 py-1 rounded-full border border-gray-200 text-xs text-gray-600 hover:border-primary-300 hover:text-primary-700 disabled:opacity-50"
                        data-testid={`suggest-section-${name}`}
                      >
                        + {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {showTags && (
            <div className="mt-4 lg:mt-0 lg:w-60 lg:shrink-0 lg:sticky lg:top-4">
              <TimelineTagPanel tags={tags} />
            </div>
          )}
        </div>
      </div>

      <DragOverlay>
        {dnd.activeItem?.kind === 'section' && (
          <div className="bg-white rounded-xl shadow-lg border-2 border-primary-300 px-4 py-3">
            <span className="font-semibold text-sm">
              {capitalizeFirstWord(dnd.activeItem.section.name)}
            </span>
          </div>
        )}
        {dnd.activeItem?.kind === 'entry' && (
          <div className="bg-white rounded-md shadow-lg border border-primary-300 px-3 py-2 flex items-center gap-3">
            <span className="text-sm font-mono font-semibold text-primary-700 w-[4.75rem] text-right">
              {formatTimelineTime(dnd.activeItem.entry.time) || '—'}
            </span>
            <span className="text-sm font-medium">
              {capitalizeFirstWord(dnd.activeItem.entry.title)}
            </span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
